import { LayoutEditorShell } from '../../../lib/form-layout/LayoutEditorShell';
import {
  LEASE_FORM_FIELDS,
  LEASE_FORM_FIELD_NAMES,
  LEASE_DEFAULT_LAYOUT,
  type LeaseFormContext,
} from '../../../lib/form-layout/lease-fields';
import {
  useAdminActiveLeaseLayout,
  useUpdateLeaseFormLayout,
} from '../../../queries/lease-form-layout';
import { useLandLookups } from '../../../queries/land-lookups';
import type { LeaseInputSchema } from '../../../lib/schemas';

/**
 * Thin wrapper around LayoutEditorShell that wires the lease-form's
 * registry / queries / preview context.
 *
 * The lease registry's lookup-backed selects (lease_type, lease_ownership,
 * fed_rights, status) read their options off `context.lookups` — same
 * pattern as the entry editor threading `docTypes`/`prospects` through
 * `previewContext`. The query hook is called here, once, and never inside
 * the renderer's per-field loop (which would break the Rules of Hooks).
 */
export function LeaseFormLayoutEditor() {
  const { data: lookups } = useLandLookups();

  const previewDefaults: LeaseInputSchema = {
    lease_number: '',
    alt_lease_number: null,
    lease_type: null,
    lease_ownership: null,
    fed_rights: null,
    status: null,
    prospect: null,
    field: null,
    reviewer_user_id: null,
    lessor: null,
    lessee: null,
    county: null,
    state: null,
    township: null,
    range: null,
    section: null,
    block: null,
    lease_date: null,
    expire_date: null,
    term: null,
    book: null,
    page: null,
    entry: null,
    provided_description: null,
    description: null,
    gross_acres: null,
    net_acres: null,
    co_net_acres: null,
    orri: false,
    royalty: false,
    lease_on_minerals: false,
    depth_limitations: false,
    depth_limitations_remark: null,
    area_pugh: false,
    depth_pugh: false,
    remarks: null,
    reviewer_notes: null,
    change_remark: null,
  };

  const previewContext: LeaseFormContext = {
    lookups: lookups ?? {
      lease_type: [],
      lease_status: [],
      lease_ownership: [],
      fed_rights: [],
      contract_type: [],
      flag_for_issues: [],
      well_status: [],
      well_type: [],
      unit_type: [],
    },
    // The layout editor's preview form has no real project/session behind
    // it, so the reviewer Select has no members to offer — canAssignAnyReviewer:
    // true just keeps the (empty) option list unfiltered rather than
    // collapsing to a self-only view that can never match anything here.
    members: [],
    canAssignAnyReviewer: true,
    currentUserId: 'preview',
  };

  return (
    <LayoutEditorShell
      title="Lease form layout"
      breadcrumbLabel="Lease form layout"
      registry={LEASE_FORM_FIELDS}
      allNames={LEASE_FORM_FIELD_NAMES}
      defaultLayout={LEASE_DEFAULT_LAYOUT}
      useActive={useAdminActiveLeaseLayout}
      useUpdate={useUpdateLeaseFormLayout}
      previewDefaults={previewDefaults}
      previewContext={previewContext}
    />
  );
}
